export function eligibleReviews(reviews, excludedPath, excludedId) {
  const seen = new Set();
  return (Array.isArray(reviews) ? reviews : []).filter((review) => {
    const score = Number(review.rating);
    const path = String(review.product_url || '').split('?')[0].replace(/\/$/, '');
    const excluded = path === excludedPath || path.endsWith(excludedPath) || String(review.encoded_product_id || '') === excludedId;
    const key = String(review.uuid || '');
    if (excluded || !key || seen.has(key) || !Number.isFinite(score) || score < 1 || score > 5) return false;
    seen.add(key);
    return true;
  }).sort((a, b) => Date.parse(b.created_at) - Date.parse(a.created_at));
}

export function reviewSummary(reviews) {
  const count = reviews.length;
  return { count, average: count ? reviews.reduce((sum, review) => sum + Number(review.rating), 0) / count : 0 };
}

export function reviewPage(reviews, page, size = 5) {
  const totalPages = Math.ceil(reviews.length / size);
  const currentPage = Math.max(1, Math.min(totalPages || 1, Number(page) || 1));
  return { currentPage, totalPages, reviews: reviews.slice((currentPage - 1) * size, currentPage * size) };
}

export function paginationItems(page, total) {
  if (total <= 7) return Array.from({ length: total }, (_, index) => index + 1);
  if (page <= 4) return [1, 2, 3, 4, 5, null, total];
  if (page >= total - 3) return [1, null, total - 4, total - 3, total - 2, total - 1, total];
  return [1, null, page - 1, page, page + 1, null, total];
}

export function reviewPhotos(reviews) {
  const seen = new Set();
  return reviews.flatMap((review) => (review.pictures_urls || []).flatMap((picture) => {
    const original = picture.original || picture.huge;
    const thumbnail = picture.compact || picture.small || original;
    try {
      const full = new URL(original);
      const thumb = new URL(thumbnail);
      if (full.protocol !== 'https:' || thumb.protocol !== 'https:' || seen.has(full.href)) return [];
      seen.add(full.href);
      return [{ review, original: full.href, thumbnail: thumb.href }];
    } catch { return []; }
  }));
}

export function translationCandidates(reviews, locale) {
  const target = String(locale || '').toLowerCase().replaceAll('_', '-').split('-')[0];
  return reviews.filter((review) => {
    const source = String(review.language || '').toLowerCase().replaceAll('_', '-').split('-')[0];
    return review.uuid && (!source || source !== target) && (review.body || review.body_html || review.title);
  });
}

export async function fetchReviewTranslations(config, reviews, request = fetch) {
  if (!reviews.length) return {};
  const url = new URL('https://api.judge.me/api/review_translations');
  url.search = new URLSearchParams({ shop_domain: config.shopDomain, platform: 'shopify', review_uuids: reviews.map((review) => review.uuid).join(','), language: config.locale, page: '-1' });
  const response = await request(url, { credentials: 'omit', signal: config.signal ? AbortSignal.any([config.signal, AbortSignal.timeout(15000)]) : AbortSignal.timeout(15000) });
  if (!response.ok) throw new Error('Review translation failed');
  const data = await response.json();
  return Object.fromEntries(reviews.flatMap((review) => {
    const result = data?.[review.uuid];
    if (result?.body?.success !== true || typeof result.body.translated_text !== 'string' || !result.body.translated_text.trim()) return [];
    return [[review.uuid, { title: result.title?.success === true && typeof result.title.translated_text === 'string' ? result.title.translated_text : review.title || '', body: result.body.translated_text }]];
  }));
}

export async function loadReviewPages(config, request = fetch) {
  async function loadType(type) {
    const rows = [];
    let pages = 1;
    for (let page = 1; page <= pages; page += 1) {
      const url = new URL('https://api.judge.me/api/v1/widgets/all_reviews_page');
      url.search = new URLSearchParams({ shop_domain: config.shopDomain, api_token: config.publicToken, review_type: type, json_request: 'true', per_page: '100', page: String(page) });
      const response = await request(url, { credentials: 'omit', signal: config.signal ? AbortSignal.any([config.signal, AbortSignal.timeout(15000)]) : AbortSignal.timeout(15000) });
      if (!response.ok) throw new Error('Review request failed');
      const data = await response.json();
      if (!Array.isArray(data.reviews)) throw new Error('Invalid review response');
      pages = Math.max(1, Number(data.total_pages) || 1);
      if (!Number.isSafeInteger(pages) || pages > 1000) throw new Error('Invalid review pagination');
      rows.push(...data.reviews.map((review) => ({ ...review, is_shop_review: type === 'shop-reviews' })));
    }
    return rows;
  }
  const streams = await Promise.all([loadType('product-reviews'), loadType('shop-reviews')]);
  return eligibleReviews(streams.flat(), config.excludedPath, config.excludedId);
}

function initialize(root) {
  const controller = new AbortController();
  const signal = controller.signal;
  const payload = root.querySelector('[data-shared-review-data]');
  if (!payload) return () => controller.abort();
  const config = { ...JSON.parse(payload.textContent), signal };
  const list = root.querySelector('[data-shared-review-list]');
  const pages = root.querySelector('[data-shared-review-pages]');
  const status = root.querySelector('[data-shared-review-status]');
  const gallery = root.querySelector('[data-shared-review-gallery]');
  const photos = root.querySelector('[data-shared-review-photos]');
  const photoPrevious = root.querySelector('[data-shared-review-photo-previous]');
  const photoNext = root.querySelector('[data-shared-review-photo-next]');
  const write = root.querySelector('[data-shared-review-write]');
  const nativeHost = root.querySelector('[data-native-review-form]');
  const badge = document.querySelector('[data-shared-review-badge="' + root.id + '"]');
  const locale = config.locale || document.documentElement.lang || 'en';
  const number = new Intl.NumberFormat(locale, { maximumFractionDigits: 1, minimumFractionDigits: 1 });
  const countryNames = typeof Intl.DisplayNames === 'function' ? new Intl.DisplayNames([locale, 'en'], { type: 'region', fallback: 'none' }) : null;
  const judgeSettings = window.jdgmSettings || {};
  const translationEnabled = judgeSettings.widget_translate_review_content_enabled === true;
  const automaticTranslation = translationEnabled && judgeSettings.widget_translate_review_content_method === 'automatic';
  const translationLocale = judgeSettings.enable_multi_locales_translations ? locale : judgeSettings.shop_locale || judgeSettings.locale || locale;
  const translations = new Map();
  const originalReviews = new Set();
  const translatedReviews = new Set();
  const translationAttempts = new Set();
  const translationPending = new Set();
  const translationErrors = new Set();
  let reviewsVisible = false;
  let reviews = eligibleReviews(config.reviews, config.excludedPath, config.excludedId);
  let currentPage = 1;

  function element(tag, className, text) {
    const node = document.createElement(tag);
    if (className) node.className = className;
    if (text !== undefined) node.textContent = text;
    return node;
  }

  function plainText(value) {
    return new DOMParser().parseFromString(String(value || ''), 'text/html').body.textContent || '';
  }

  function safeUrl(value) {
    try {
      const url = new URL(value, location.origin);
      return url.protocol === 'https:' ? url.href : '';
    } catch { return ''; }
  }

  function stars(score) {
    const outer = element('span', 'sasha-review-stars');
    outer.setAttribute('role', 'img');
    outer.setAttribute('aria-label', String(score) + '/5');
    outer.append(element('span', '', '★★★★★'));
    const fill = element('span', 'sasha-review-stars__fill', '★★★★★');
    fill.style.width = String(Math.max(0, Math.min(100, Number(score) * 20))) + '%';
    outer.append(fill);
    return outer;
  }

  function verifiedBadge() {
    const badge = element('span', 'sasha-review__verified');
    const svg = document.createElementNS('http://www.w3.org/2000/svg', 'svg');
    svg.setAttribute('viewBox', '0 0 24 24');
    svg.setAttribute('aria-hidden', 'true');
    svg.setAttribute('fill', 'none');
    const circle = document.createElementNS('http://www.w3.org/2000/svg', 'circle');
    circle.setAttribute('cx', '12');
    circle.setAttribute('cy', '12');
    circle.setAttribute('r', '9');
    circle.setAttribute('stroke', 'currentColor');
    circle.setAttribute('stroke-width', '1.6');
    const check = document.createElementNS('http://www.w3.org/2000/svg', 'path');
    check.setAttribute('d', 'm8 12 2.5 2.5L16 9');
    check.setAttribute('stroke', 'currentColor');
    check.setAttribute('stroke-width', '1.6');
    check.setAttribute('stroke-linecap', 'round');
    check.setAttribute('stroke-linejoin', 'round');
    svg.append(circle, check);
    badge.append(svg, document.createTextNode(config.verifiedBuyerLabel));
    return badge;
  }

  function reviewCountry(review) {
    // Match Judge.me's public flag field, including its choice to omit a country.
    const code = String(review.country_code_show_flag || '').trim().toUpperCase();
    if (!/^[A-Z]{2}$/.test(code)) return null;
    const country = element('span', 'sasha-review__country');
    const countryName = countryNames?.of(code) || review.location_country || code;
    country.title = countryName;
    const flag = element('img');
    flag.src = 'https://cdn.shopify.com/static/images/flags/' + code.toLowerCase() + '.svg?width=32';
    flag.alt = countryName;
    flag.width = 16;
    flag.height = 12;
    flag.loading = 'lazy';
    flag.decoding = 'async';
    country.append(flag);
    return country;
  }

  function renderReview(review) {
    const row = element('article', 'sasha-review');
    row.dataset.reviewId = review.uuid;
    const head = element('div', 'sasha-review__head');
    head.append(stars(review.rating));
    head.append(element('span', 'sasha-review__author', review.reviewer_name || ''));
    const country = reviewCountry(review);
    if (country) head.append(country);
    if (review.verified_buyer) head.append(verifiedBadge());
    row.append(head);
    const translated = translations.get(review.uuid);
    const showingTranslation = !!translated && !originalReviews.has(review.uuid) && (automaticTranslation || translatedReviews.has(review.uuid));
    const titleText = showingTranslation ? plainText(translated.title) : review.title;
    if (titleText) row.append(element('h3', 'sasha-review__title', titleText));
    row.append(element('p', 'sasha-review__body', showingTranslation ? plainText(translated.body) : review.body || plainText(review.body_html)));
    const localized = review.product_title_localized || {};
    const title = review.is_shop_review ? config.shopName : localized[locale] || localized[locale.split('-')[0]] || review.product_title;
    const sourcePath = String(review.product_url || '');
    const productPath = sourcePath.startsWith('/products/') ? String(config.rootUrl || '/').replace(/\/$/, '') + sourcePath : sourcePath;
    const productUrl = safeUrl(productPath);
    const source = element(productUrl ? 'a' : 'span', 'sasha-review__product', title || config.shopName);
    if (productUrl) source.href = productUrl;
    const footer = element('div', 'sasha-review__footer');
    footer.append(source);
    if (translationEnabled && !automaticTranslation && translationCandidates([review], translationLocale).length) {
      const label = translationPending.has(review.uuid) ? config.translatingLabel : translationErrors.has(review.uuid) ? config.translationRetryLabel : showingTranslation ? config.showOriginalLabel : config.showTranslationLabel;
      const toggle = element('button', 'sasha-review__translation', label);
      toggle.type = 'button';
      toggle.dataset.reviewTranslation = review.uuid;
      toggle.setAttribute('aria-pressed', String(showingTranslation));
      toggle.disabled = translationPending.has(review.uuid);
      footer.append(toggle);
    }
    row.append(footer);
    if (review.reply_content) {
      const reply = element('div', 'sasha-review__reply');
      reply.append(element('strong', '', (review.shop_reply_name || config.shopName) + ': '));
      reply.append(element('span', '', plainText(review.reply_content)));
      row.append(reply);
    }
    return row;
  }

  function renderList() {
    const focusedReview = list.contains(document.activeElement) ? document.activeElement.dataset?.reviewTranslation : null;
    const page = reviewPage(reviews, currentPage);
    currentPage = page.currentPage;
    list.replaceChildren(...page.reviews.map(renderReview));
    pages.hidden = page.totalPages <= 1;
    pages.replaceChildren(...paginationItems(currentPage, page.totalPages).map((value) => {
      if (value === null) {
        const gap = element('span', 'sasha-review-pagination__gap', '…');
        gap.setAttribute('aria-hidden', 'true');
        return gap;
      }
      const button = element('button', 'sasha-review-pagination__page', String(value));
      button.type = 'button';
      button.dataset.reviewPage = String(value);
      button.setAttribute('aria-label', config.pageLabel.replace('__PAGE__', String(value)));
      button.setAttribute('aria-controls', list.id);
      if (value === currentPage) button.setAttribute('aria-current', 'page');
      return button;
    }));
    if (focusedReview) [...list.querySelectorAll('[data-review-translation]')].find((button) => button.dataset.reviewTranslation === focusedReview)?.focus({ preventScroll: true });
    if (automaticTranslation && reviewsVisible) translateVisibleReviews();
  }

  async function translateReviews(rows) {
    const pending = rows.filter((review) => !translationPending.has(review.uuid) && !translations.has(review.uuid));
    if (!pending.length) return;
    for (const review of pending) {
      translationPending.add(review.uuid);
      translationAttempts.add(review.uuid);
      translationErrors.delete(review.uuid);
    }
    renderList();
    try {
      const translated = await fetchReviewTranslations({ ...config, locale: translationLocale }, pending);
      if (signal.aborted || !root.isConnected) return;
      for (const review of pending) {
        if (translated[review.uuid]) translations.set(review.uuid, translated[review.uuid]);
        else translationErrors.add(review.uuid);
      }
    } catch {
      if (signal.aborted) return;
      for (const review of pending) translationErrors.add(review.uuid);
    } finally {
      for (const review of pending) translationPending.delete(review.uuid);
      if (!signal.aborted && root.isConnected) renderList();
    }
  }

  function translateVisibleReviews() {
    const pending = translationCandidates(reviewPage(reviews, currentPage).reviews, translationLocale).filter((review) => !translationAttempts.has(review.uuid));
    if (pending.length) translateReviews(pending);
  }

  function updateGalleryControls() {
    const overflow = photos.scrollWidth > photos.clientWidth + 2;
    const position = Math.abs(photos.scrollLeft);
    photoPrevious.hidden = !overflow;
    photoNext.hidden = !overflow;
    photoPrevious.disabled = position <= 8;
    photoNext.disabled = position >= photos.scrollWidth - photos.clientWidth - 8;
  }

  function renderGallery() {
    const pictures = reviewPhotos(reviews);
    gallery.hidden = pictures.length === 0;
    photos.replaceChildren(...pictures.map(({ review, original, thumbnail }) => {
      const link = element('a', 'sasha-review-gallery__photo');
      link.href = original;
      link.target = '_blank';
      link.rel = 'noopener';
      link.dataset.reviewId = review.uuid;
      const image = element('img');
      image.src = thumbnail;
      image.alt = review.product_title_localized?.[locale] || review.product_title || config.shopName;
      image.loading = 'lazy';
      image.width = 160;
      image.height = 160;
      link.append(image);
      return link;
    }));
    updateGalleryControls();
  }

  function movePhotos(direction) {
    const rtl = getComputedStyle(photos).direction === 'rtl' ? -1 : 1;
    photos.scrollBy({ left: direction * rtl * Math.max(160, photos.clientWidth * 0.8), behavior: matchMedia('(prefers-reduced-motion: reduce)').matches ? 'instant' : 'smooth' });
  }

  function updateSummary(summary) {
    root.hidden = summary.count === 0;
    if (badge) {
      badge.hidden = summary.count === 0;
      badge.setAttribute('aria-label', number.format(summary.average) + '/5 · ' + summary.count + ' ' + config.reviewsLabel);
    }
    for (const host of [root, badge].filter(Boolean)) {
      host.querySelectorAll('[data-shared-review-count]').forEach((node) => { node.textContent = String(summary.count); });
      host.querySelectorAll('[data-shared-review-average]').forEach((node) => { node.textContent = number.format(summary.average); });
      host.querySelectorAll('.sasha-review-stars__fill').forEach((node) => {
        if (!node.closest('.sasha-review')) node.style.width = String(summary.average * 20) + '%';
      });
    }
    root.dataset.reviewCount = String(summary.count);
    root.dataset.averageRating = String(summary.average);
  }

  function connectForm() {
    const nativeButton = nativeHost.querySelector('.jm-action-buttons__button, .jdgm-write-rev-link');
    if (!nativeButton) return;
    write.textContent = nativeButton.textContent.trim();
    write.hidden = !write.textContent;
    nativeHost.querySelectorAll('a, button, input, select, textarea, [tabindex]').forEach((node) => { node.tabIndex = -1; });
  }

  write.addEventListener('click', () => {
    nativeHost.querySelector('.jm-action-buttons__button, .jdgm-write-rev-link')?.click();
  }, { signal });
  list.addEventListener('click', (event) => {
    const button = event.target.closest('button[data-review-translation]');
    if (!button || !list.contains(button)) return;
    const id = button.dataset.reviewTranslation;
    const review = reviews.find((item) => item.uuid === id);
    if (!review) return;
    const showingTranslation = translations.has(id) && !originalReviews.has(id) && (automaticTranslation || translatedReviews.has(id));
    if (showingTranslation) originalReviews.add(id);
    else {
      originalReviews.delete(id);
      translatedReviews.add(id);
      if (!translations.has(id)) { translateReviews([review]); return; }
    }
    renderList();
  }, { signal });
  const translationObserver = automaticTranslation ? new IntersectionObserver((entries) => {
    if (entries.some((entry) => entry.isIntersecting)) {
      reviewsVisible = true;
      translationObserver.disconnect();
      translateVisibleReviews();
    }
  }, { rootMargin: '150px' }) : null;
  translationObserver?.observe(root);
  pages.addEventListener('click', (event) => {
    const button = event.target.closest('button[data-review-page]');
    if (!button || !pages.contains(button) || Number(button.dataset.reviewPage) === currentPage) return;
    currentPage = Number(button.dataset.reviewPage);
    renderList();
    pages.querySelector('[aria-current="page"]')?.focus({ preventScroll: true });
    status.textContent = config.pageLabel.replace('__PAGE__', String(currentPage));
    list.scrollIntoView({ behavior: matchMedia('(prefers-reduced-motion: reduce)').matches ? 'instant' : 'smooth', block: 'start' });
  }, { signal });
  photoPrevious.addEventListener('click', () => movePhotos(-1), { signal });
  photoNext.addEventListener('click', () => movePhotos(1), { signal });
  photos.addEventListener('scroll', updateGalleryControls, { signal, passive: true });
  photos.addEventListener('keydown', (event) => {
    if (event.target !== photos || !['ArrowLeft', 'ArrowRight'].includes(event.key)) return;
    event.preventDefault();
    const rtl = getComputedStyle(photos).direction === 'rtl' ? -1 : 1;
    movePhotos((event.key === 'ArrowRight' ? 1 : -1) * rtl);
  }, { signal });
  const galleryObserver = new ResizeObserver(updateGalleryControls);
  galleryObserver.observe(photos);
  badge?.addEventListener('click', (event) => {
    event.preventDefault();
    root.scrollIntoView({ behavior: matchMedia('(prefers-reduced-motion: reduce)').matches ? 'auto' : 'smooth', block: 'start' });
    root.focus({ preventScroll: true });
  }, { signal });
  const observer = new MutationObserver(connectForm);
  observer.observe(nativeHost, { childList: true, subtree: true });
  connectForm();
  renderList();
  renderGallery();
  if (config.complete) updateSummary(reviewSummary(reviews));
  if (!config.complete) {
    loadReviewPages(config).then((loaded) => {
      if (signal.aborted || !root.isConnected) return;
      reviews = loaded;
      renderList();
      renderGallery();
      updateSummary(reviewSummary(reviews));
    }).catch(() => { if (!signal.aborted) root.dataset.reviewLoadFailed = 'true'; });
  }
  return () => { controller.abort(); observer.disconnect(); galleryObserver.disconnect(); translationObserver?.disconnect(); };
}

if (typeof document !== 'undefined') {
  const { Component } = await import('@theme/component');
  class SashaPrintReviews extends Component {
    cleanup;
    connectedCallback() {
      super.connectedCallback();
      this.cleanup?.();
      this.cleanup = initialize(this);
    }
    updatedCallback() {
      super.updatedCallback();
      this.cleanup?.();
      this.cleanup = initialize(this);
    }
    disconnectedCallback() {
      this.cleanup?.();
      super.disconnectedCallback();
    }
  }
  if (!customElements.get('sasha-print-reviews')) customElements.define('sasha-print-reviews', SashaPrintReviews);
}
